<nav aria-label="Credential transition pages">
  <ul class="nav nav-tabs mb-3" role="tablist">
    <li class="nav-item" *ngIf="canManageProcesses">
      <a class="nav-link" href="#" role="tab" [class.active]="activeTab === 'processes'"
        [class.disabled]="busy || transitionSaving" [attr.aria-disabled]="busy || transitionSaving"
        [attr.aria-selected]="activeTab === 'processes'"
        (click)="$event.preventDefault(); activeTab = busy || transitionSaving ? activeTab : 'processes'">Process settings</a>
    </li>
    <li class="nav-item" *ngIf="canViewTransitions">
      <a class="nav-link" href="#" role="tab" [class.active]="activeTab === 'employees'"
        [class.disabled]="busy || transitionSaving" [attr.aria-disabled]="busy || transitionSaving"
        [attr.aria-selected]="activeTab === 'employees'"
        (click)="$event.preventDefault(); activeTab = busy || transitionSaving ? activeTab : 'employees'">Employee transitions</a>
    </li>
  </ul>
</nav>
<app-employee-transitions *ngIf="activeTab === 'employees' && canViewTransitions"></app-employee-transitions>
<div class="card mt-2" *ngIf="activeTab === 'processes' && canManageProcesses">
  <div class="card-body">
    <h2 class="mb-3">Process settings</h2>
    <p>Configure employee-initiated credential transitions and their pay adjustments. Credential verification is always required. Existing employee offer forms and unchanged employment terms are reused.</p>
    <p class="alert alert-danger" *ngIf="error" role="alert">{{error}}</p>
    <button class="btn btn-primary mb-3" [disabled]="busy || !!selected" (click)="create()">Create process</button>
    <p *ngIf="busy" role="status">Loading or saving…</p>
    <div class="table-responsive" *ngIf="!selected">
      <table class="table table-striped">
        <thead>
          <tr>
            <th>Name</th>
            <th>Credentials</th>
            <th>Version</th>
            <th>Status</th>
            <th>Active transitions</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let p of processes">
            <td>{{p.name}}</td>
            <td>{{label(p.sourceCredentialID, 'credentials')}} → {{label(p.targetCredentialID, 'credentials')}}</td>
            <td>{{p.versionNumber}}</td>
            <td>{{p.archived ? 'Archived' : p.enabled ? 'New starts enabled' : 'New starts disabled'}}{{p.paused ? ' / Paused' : ''}}</td>
            <td>{{p.activeTransitions}}</td>
            <td>
              <button class="btn btn-outline-primary mr-2 mb-2" [disabled]="busy" (click)="edit(p)">Edit</button>
              <button
                class="btn btn-outline-secondary mr-2 mb-2"
                *ngIf="!p.archived"
                [disabled]="busy"
                (click)="control(p, 'enabled')"
              >{{p.enabled ? 'Disable new starts' : 'Enable new starts'}}</button>
              <button class="btn btn-outline-secondary mr-2 mb-2" [disabled]="busy" (click)="control(p, 'paused')">{{p.paused ? 'Resume' : 'Pause offers'}}</button>
              <button class="btn btn-outline-danger mb-2" *ngIf="!p.archived" [disabled]="busy" (click)="remove(p)">Delete / archive</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <form *ngIf="selected" (ngSubmit)="save()" #editor="ngForm">
      <fieldset [disabled]="busy" class="card card-body">
        <h2 class="h4">{{selected.id ? 'Edit process — new settings version' : 'Create process'}}</h2>
        <div class="form-group">
          <label for="ct-name">Name</label>
          <input id="ct-name" class="form-control" name="name" [(ngModel)]="selected.name" required maxlength="200">
        </div>
        <div class="form-group">
          <label for="ct-description">Description</label>
          <textarea
            id="ct-description"
            class="form-control"
            name="description"
            [(ngModel)]="selected.description"
            maxlength="1000"
          ></textarea>
        </div>
        <div class="row">
          <div class="form-group col-md-6">
            <label for="ct-source">Existing credential</label>
            <select id="ct-source" class="form-control" name="source" [(ngModel)]="selected.sourceCredentialID" required>
              <option [ngValue]="null">Select a credential</option>
              <option *ngFor="let c of lookups.credentials" [ngValue]="c.id">{{c.name}}</option>
            </select>
          </div>
          <div class="form-group col-md-6">
            <label for="ct-target">New credential</label>
            <select id="ct-target" class="form-control" name="target" [(ngModel)]="selected.targetCredentialID" required>
              <option [ngValue]="null">Select a credential</option>
              <option *ngFor="let c of lookups.credentials" [ngValue]="c.id">{{c.name}}</option>
            </select>
          </div>
        </div>
        <label><input type="checkbox" name="enabled" [(ngModel)]="selected.enabled"> Allow new starts</label>
        <label><input type="checkbox" name="paused" [(ngModel)]="selected.paused"> Pause offer generation and sending</label>
        <label *ngIf="selected.archived"><input type="checkbox" name="archived" [(ngModel)]="selected.archived"> Archived</label>
        <h3 class="h5 mt-3">Requirements</h3>
        <p>Employees need an unexpired source credential and must not already have the target credential to begin. Target credential verification is mandatory.</p>
        <label><input type="checkbox" name="exam" [(ngModel)]="selected.requireExamDate"> Require exam date</label>
        <label><input type="checkbox" name="upload" [(ngModel)]="selected.requireCredentialUpload"> Require credential upload</label>
        <label><input
            type="checkbox"
            name="kra"
            [(ngModel)]="selected.requireKra"
            [disabled]="selected.compensationMode === 2"
          > Require paired KRA Self and Oversight Reviews</label>
        <div *ngIf="selected.requireKra || selected.compensationMode === 2">
          <div class="form-group">
            <label for="ct-template">KRA template</label>
            <select
              id="ct-template"
              class="form-control"
              name="template"
              [(ngModel)]="selected.employeeReviewTemplateID"
              required
            >
              <option [ngValue]="null">Select a template</option>
              <option *ngFor="let t of lookups.templates" [ngValue]="t.id">{{t.name}}</option>
            </select>
          </div>
          <div class="form-group">
            <label for="ct-validity">KRA validity, including completion date (calendar days)</label>
            <input
              id="ct-validity"
              type="number"
              class="form-control"
              name="validity"
              [(ngModel)]="selected.kraValidityDays"
              required
              min="1"
              max="3650"
            >
          </div>
          <label><input type="checkbox" name="reuse" [(ngModel)]="selected.reuseValidKra"> Reuse the latest matching self and oversight submissions within the validity period</label>
          <p>Each review's validity starts on its submission date. Existing oversight submissions may await required approval. An offer must be completed before either review expires.</p>
        </div>
        <h3 class="h5 mt-3">Compensation</h3>
        <select
          class="form-control mb-3"
          aria-label="Compensation mode"
          name="mode"
          [(ngModel)]="selected.compensationMode"
          (ngModelChange)="selected.requireKra = selected.compensationMode === 2 || selected.requireKra"
        >
          <option [ngValue]="1">Fixed hourly rate</option>
          <option [ngValue]="2">KRA score-based hourly rates</option>
        </select>
        <div class="form-group" *ngIf="selected.compensationMode === 1">
          <label for="ct-fixed">Hourly rate</label>
          <input
            id="ct-fixed"
            type="number"
            class="form-control"
            name="fixed"
            [(ngModel)]="selected.fixedHourlyRate"
            required
            min="0.0001"
            step="0.0001"
          >
        </div>
        <div *ngIf="selected.compensationMode === 2">
          <p>The highest minimum score met determines the rate. Each minimum score can have only one rate.</p>
          <div class="row mb-2" *ngFor="let r of selected.rates; let i = index">
            <div class="col">
              <label>Minimum score<input
                  type="number"
                  class="form-control"
                  [name]="'score'+i"
                  [(ngModel)]="r.minimumScore"
                  required
                  min="0"
                  step="0.0001"
                ></label>
            </div>
            <div class="col">
              <label>Hourly rate<input
                  type="number"
                  class="form-control"
                  [name]="'rate'+i"
                  [(ngModel)]="r.hourlyRate"
                  required
                  min="0.0001"
                  step="0.0001"
                ></label>
            </div>
            <div class="col">
              <button type="button" class="btn btn-outline-danger" (click)="selected.rates.splice(i,1)">Remove</button>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-outline-primary mb-3"
            (click)="selected.rates.push({minimumScore: null, hourlyRate: null})"
          >Add rate</button>
        </div>
        <div class="form-group">
          <label for="ct-training">Training rate (optional)</label>
          <input
            id="ct-training"
            type="number"
            class="form-control"
            name="training"
            [(ngModel)]="selected.trainingRate"
            min="0"
            step="0.0001"
          >
        </div>
        <h3 class="h5 mt-3">Resulting titles</h3>
        <div class="form-group">
          <label for="ct-job">Job Title</label>
          <select id="ct-job" class="form-control" name="job" [(ngModel)]="selected.jobTitleID" required>
            <option [ngValue]="null">Select a title</option>
            <option *ngFor="let t of lookups.jobTitles" [ngValue]="t.id">{{t.name}}</option>
          </select>
        </div>
        <label><input type="checkbox" name="same" [(ngModel)]="sameTitle"> Use the same Clinical Job Title</label>
        <div class="form-group" *ngIf="!sameTitle">
          <label for="ct-clinical">Clinical Job Title</label>
          <select
            id="ct-clinical"
            class="form-control"
            name="clinical"
            [(ngModel)]="selected.clinicalJobTitleID"
            required
          >
            <option [ngValue]="null">Select a title</option>
            <option *ngFor="let t of lookups.jobTitles" [ngValue]="t.id">{{t.name}}</option>
          </select>
        </div>
        <label><input type="checkbox" name="signature" [(ngModel)]="selected.showSignaturePrompt"> Prompt employee to update professional signature after completion</label>
        <label *ngIf="selected.id"><input type="checkbox" name="apply" [(ngModel)]="selected.applyToExisting"> Apply these requirements to existing transitions that have never generated an offer</label>
        <p *ngIf="selected.id">Other transitions retain their assigned version. Generated offers are preserved; expired offers renew under their assigned version.</p>
        <div class="mt-3">
          <button class="btn btn-primary mr-2" type="submit" [disabled]="!editor.valid || busy">Save</button>
          <button class="btn btn-secondary" type="button" (click)="selected = null">Cancel</button>
        </div>
      </fieldset>
    </form>
  </div>
</div>
